'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import PropTypes from 'prop-types';
import BigText from 'ecosystem-marketplace-core/components/elements/BigText';
import Rating from 'ecosystem-marketplace-core/components/reviews/Rating';
import ReviewBody from 'ecosystem-marketplace-core/components/reviews/ReviewBody';
import ReviewerInfo from 'ecosystem-marketplace-core/components/reviews/ReviewerInfo';
import ReviewReplyThread from 'ecosystem-marketplace-core/components/reviews/ReviewReplyThread';
import ReviewActionsWrapper from 'ecosystem-marketplace-core/components/reviews/ReviewActionsWrapper';
import HelpfulButton from 'ecosystem-marketplace-core/components/reviews/HelpfulButton';
import ReportButton from 'ecosystem-marketplace-core/components/reviews/ReportButton';
import ReportReviewModal from 'ecosystem-marketplace-core/components/reviews/modals/ReportReviewModal';
import { GREAT_WHITE } from 'HubStyleTokens/colors';
import { Reportables } from 'ecosystem-marketplace-core/constants/reviews/ReportReasons';
import { RatingAxes } from 'ecosystem-marketplace-core/constants/reviews/RatingAxes';
import Review from 'ecosystem-marketplace-core/records/Review';
import UISection from 'UIComponents/section/UISection';
import { makeGetUserMarkedHelpfulSelector } from '../../selectors/reviews';
import { reportReply, reportReview, toggleReviewHelpful } from '../../actions/ReviewsActions';
import { getIsCustomer, getUserId } from '../../selectors/auth';

const ThemeReviewRenderer = ({
  borderBottom,
  review,
  searchTerm
}) => {
  const dispatch = useDispatch();
  const [reportModalType, setReportModalType] = useState(null);
  const isCustomer = useSelector(getIsCustomer);
  const userId = useSelector(getUserId);
  const getUserMarkedHelpful = useMemo(makeGetUserMarkedHelpfulSelector, []);
  const userMarkedHelpful = useSelector(state => getUserMarkedHelpful(state, review.get('id')));
  const handleClickHelpful = useCallback(() => {
    dispatch(toggleReviewHelpful(review.get('id'), userId, !userMarkedHelpful));
  }, [dispatch, review, userId, userMarkedHelpful]);
  const renderedReportModal = useMemo(() => {
    const onConfirmAction = (reason, comment) => reportModalType === Reportables.REVIEW ? reportReview(review.get('id'), reason, comment) : reportReply(review.get('id'), review.getIn(['reply', 'id']), reason, comment);

    return /*#__PURE__*/_jsx(ReportReviewModal, {
      review: review,
      type: reportModalType,
      onClose: () => {
        setReportModalType(null);
      },
      onConfirm: (reason, comment) => {
        dispatch(onConfirmAction(reason, comment));
        setReportModalType(null);
      }
    });
  }, [dispatch, review, reportModalType]);
  const reviewActions = useMemo(() => {
    return /*#__PURE__*/_jsxs(ReviewActionsWrapper, {
      children: [/*#__PURE__*/_jsx(HelpfulButton, {
        className: "m-right-2",
        disabled: !isCustomer,
        helpfulCount: review.getHelpfulCount(),
        onClickHelpful: handleClickHelpful,
        userMarkedHelpful: userMarkedHelpful
      }), /*#__PURE__*/_jsx(ReportButton, {
        className: "m-left-2",
        isCustomer: isCustomer,
        onClick: () => setReportModalType(Reportables.REVIEW)
      })]
    });
  }, [handleClickHelpful, isCustomer, review, userMarkedHelpful]);
  return /*#__PURE__*/_jsxs("div", {
    className: "p-y-8",
    style: {
      width: '100%',
      borderBottom: borderBottom && `1px solid ${GREAT_WHITE}`
    },
    children: [/*#__PURE__*/_jsx(UISection, {
      children: /*#__PURE__*/_jsx(Rating, {
        rating: review.get('ratings').get(RatingAxes.OVERALL),
        label: /*#__PURE__*/_jsx(FormattedDateTime, {
          value: review.get('createdAt'),
          format: "ll",
          type: "userTz"
        })
      })
    }), /*#__PURE__*/_jsx(ReviewBody, {
      AnswerText: BigText,
      review: review,
      searchTerm: searchTerm
    }), /*#__PURE__*/_jsx(ReviewerInfo, {
      review: review,
      inline: true
    }), reviewActions, /*#__PURE__*/_jsx(ReviewReplyThread, {
      reply: review.get('reply'),
      onReportReplyClick: () => setReportModalType(Reportables.REPLY)
    }), reportModalType && renderedReportModal]
  });
};

ThemeReviewRenderer.propTypes = {
  borderBottom: PropTypes.bool,
  review: PropTypes.instanceOf(Review).isRequired,
  searchTerm: PropTypes.string.isRequired
};
export default ThemeReviewRenderer;